<template>
  <div class="page-card-wrapper">
    <div class="page-card-title">
      <span class="page-card-title-text">{{ $t('text.network_map') }}</span>
    </div>
    <div class="page-card-content">
      <div class="text-right mt-10">
        <el-checkbox v-model="zoomView">{{
          $t('text.zoom_in_chart')
        }}</el-checkbox>
      </div>
      <div v-if="loading" class="loading-chart-wrapper">
        <LoadingAnimation />
      </div>
      <div v-else>
        <div id="network-map" v-if="nodes.length"></div>
        <div v-else class="no-data min-h-300 mt-20 v-h-center wp-100">
          <b>{{ $t('text.no_nodes') }}</b>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { DataSet, Network } from 'vis-network/standalone'
import ApiService from '@/services/ApiService.js'
import moment from 'moment'
import LoadingAnimation from '@/components/patients/charts/LoadingAnimation'
import swal from 'sweetalert'
import _ from 'lodash'
export default {
  data() {
    return {
      zoomView: false,
      loading: false,
      nodes: [],
      edges: [],
      chart: null,
      timer: null
    }
  },
  components: {
    LoadingAnimation
  },
  mounted() {
    this.getData()
    this.timer = setInterval(this.getData, 180000)
  },
  watch: {
    zoomView() {
      this.initChart()
    },
    '$store.state.locale.currentLocale': function() {
      this.getData()
    },
    '$store.state.filter.dashboardFilter': {
      handler: _.debounce(function() {
        this.getData()
      }, 100),
      deep: true
    }
  },
  methods: {
    getData() {
      if (!this.$store.getters[`user/selecteOrganisationId`]) return false
      this.loading = true
      if (this.chart) {
        this.chart.destroy()
      }
      this.nodes = this.edges = []
      this.getSiteNodes().then(() => {
        this.getSiteMesh().then(() => {
          this.loading = false
          if (this.nodes.length) {
            this.$nextTick(() => {
              this.initChart()
            })
          }
        })
      })
    },
    async getSiteNodes() {
      await ApiService.getSiteNodes(
        this.$store.getters[`user/selecteOrganisationId`]
      ).then(response => {
        if (response.data.success) {
          this.nodes = response.data.data
        } else {
          swal({
            icon: 'error',
            timer: 2000,
            title: this.$t('text.failed'),
            text: response.data.msg
          })
        }
      })
    },
    async getSiteMesh() {
      await ApiService.getSiteMeshV2(
        this.$store.getters[`filter/calculatedFilter`].organisationId,
        this.$store.getters[`filter/calculatedFilter`].flag,
        this.$store.getters[`filter/calculatedFilter`].filterIds
      ).then(response => {
        if (response.data.success) {
          let meshNodeMacArr = response.data.data.map(item => item.NodeMac)
          this.nodes = this.nodes.filter(item =>
            meshNodeMacArr.includes(item.MacAddress)
          )
          this.nodes = this.nodes.map(node => {
            let nodeInMesh = response.data.data.filter(subItem => {
              return node.MacAddress === subItem.NodeMac
            })
            let lastSeen = ''
            if (nodeInMesh && nodeInMesh.length && nodeInMesh[0].LastSeen) {
              lastSeen = moment().diff(
                moment.utc(nodeInMesh[0].LastSeen),
                'seconds'
              )
            }
            // If lastSeen === null || lastSeen > 900 seconds, the node is offline
            let offline = !lastSeen || lastSeen > 900
            return {
              id: node.MacAddress,
              offline: offline,
              image: offline
                ? require('@/assets/image/network-img/room-red.png')
                : require('@/assets/image/network-img/room.png'),
              label: `<b>${node.BuildingName},${node.FloorName},${
                node.RoomNumber
              }</b>\n ${node.MacAddress}\n${this.$t('text.last_seen')}: ${
                lastSeen
                  ? this.formatTime(lastSeen) + `${this.$t('text.ago')}`
                  : '--'
              }\n${this.$t('text.pulse')}: ${(nodeInMesh &&
                nodeInMesh.length &&
                nodeInMesh[0].Pulse) ||
                '--'}`
            }
          })

          if (this.nodes.length) {
            let wifi = {
              id: 1,
              label: `<b>${this.$t('text.wifi_infrastructure')}</b>`,
              image: require('@/assets/image/network-img/wifi.png')
            }
            let lan = {
              id: 2,
              size: 12,
              label: `<b>${this.$t('text.lan')}</b>`,
              image: require('@/assets/image/network-img/lan.png')
            }
            this.nodes.unshift(wifi)
            this.nodes.unshift(lan)
          }

          response.data.data.forEach(edge => {
            // When a node connects multiple wifi routers, only one is displayed
            if (edge.MeshMode === 'Master' && edge.ParentMac) {
              let currentNodeExistWifi = this.edges.filter(item => {
                return item.to === edge.NodeMac && item.from === 1
              })
              if (currentNodeExistWifi.length > 0) return false
            }
            let edgeItem = {
              from: this.getFrom(edge),
              to: edge.NodeMac,
              dashes: edge.MeshMode !== 'Master',
              label: `${this.$t('text.rssi')} = ${edge.NodeRssi}`
            }
            if (this.getEdgeStatus(edgeItem)) {
              edgeItem.color = '#888'
              ;(edgeItem.label = `${this.$t('text.lost_connect')}\n `),
                (edgeItem.font = {
                  color: 'red',
                  vadjust: 25
                }),
                (edgeItem.arrows = {
                  middle: {
                    enabled: true,
                    type: 'image',
                    src: require('@/assets/image/network-img/lost-connection.png'),
                    imageWidth: 6,
                    imageHeight: 12
                  }
                })
            }
            this.edges.push(edgeItem)
          })
        } else {
          swal({
            icon: 'error',
            timer: 2000,
            title: this.$t('text.failed'),
            text: response.data.msg
          })
        }
      })
    },
    getFrom(item) {
      if (item.MeshMode === 'Master') {
        if (item.ParentMac) {
          // from wifi
          return 1
        } else {
          // from lan
          return 2
        }
      } else {
        return item.ParentMac
      }
    },
    getEdgeStatus(edge) {
      let offline = false
      // If from or to is offline, the edge will be offline
      this.nodes.forEach(item => {
        if (
          (item.id === edge.from && item.offline) ||
          (item.id === edge.to && item.offline)
        ) {
          offline = true
        }
      })
      return offline
    },
    formatTime(num) {
      if (!num) return [0, 0, 0, 0]
      let day = Math.floor(num / 86400)
      let hour = Math.floor((num - day * 86400) / 3600)
      let minute = Math.floor((num - day * 86400 - hour * 3600) / 60)
      let second = num - day * 86400 - hour * 3600 - minute * 60
      let str = ''
      if (day) str = day + 'd '
      if (hour) str = str + hour + 'h '
      if (minute) str = str + minute + 'm '
      if (second) str = str + second + 's '
      return str
    },
    initChart() {
      let container = document.getElementById('network-map')
      let options = {
        width: '100%',
        height: '100%',
        layout: {
          randomSeed: 1
        },
        interaction: {
          zoomView: this.zoomView,
          hover: true,
          tooltipDelay: 0
        },
        nodes: {
          font: {
            multi: 'html',
            size: 11,
            vadjust: -5
          }
        },
        edges: {
          length: 250,
          font: {
            multi: 'html',
            size: 11
          }
        }
      }

      let connectEdge = this.edges.filter(item => {
        return item.from && item.to
      })
      if (!connectEdge.length) {
        options.layout.hierarchical = {
          levelSeparation: 195,
          nodeSpacing: 200,
          treeSpacing: 200,
          direction: 'UD',
          sortMethod: 'directed'
        }
      }
      let nodes = new DataSet(this.nodes)
      let edges = new DataSet(this.edges)
      let data = {
        nodes: nodes.map(item => {
          return {
            ...item,
            shape: 'image'
          }
        }),
        edges: edges
      }
      this.chart = new Network(container, data, options)
    }
  },
  beforeDestroy() {
    clearInterval(this.timer)
  }
}
</script>
